import { Flex } from "@similarweb/ui-components/foundations";

import { AllowedCountriesDropdown } from "app/pages/sales-intelligence/modules/enrichment/components/AllowedCountriesDropdown/AllowedCountriesDropdown";
import type { ICountryObject } from "app/services/CountryService";

import type { CrmEnrichmentI18nKeys } from "../../../types/translations";
import type {
    CrmEnrichmentJobObject,
    CrmEnrichmentJobRefreshPeriod,
    CrmEnrichmentJobType,
    CrmEnrichmentSource,
    LeadScoreRefreshDataInterval,
} from "../../../types/types";
import EnrichmentLevelRadio from "../../settings/EnrichmentLevelRadio";
import JobObjectSwitcher from "../../settings/JobObjectSwitcher";
import LeadScoreRefreshDataIntervalDropdown from "../../settings/LeadScoreRefreshDataIntervalDropdown";
import RefreshPeriodDropdown from "../../settings/RefreshPeriodDropdown";
import {
    StyledCrmEnrichmentJobDropdownWrapper,
    StyledCrmEnrichmentJobSetting,
    StyledScheduleDropdownWrapper,
} from "../../styles";
import Caption from "../Caption";

const COUNTRY_DD_ID = "crm-enrichment-countries-dd-container";

const COUNTRIES_DD_HEIGHT = 232;

type Props = {
    selectedJobType: CrmEnrichmentJobType | "";
    selectedJobObject: CrmEnrichmentJobObject | "";
    initialJobObject?: CrmEnrichmentJobObject;
    jobObjectOptions: readonly CrmEnrichmentJobObject[];
    selectedCountry: ICountryObject;
    selectedJobRefreshPeriod: CrmEnrichmentJobRefreshPeriod;
    selectedLeadScoreRefreshDataInterval?: LeadScoreRefreshDataInterval | "";
    selectedEnrichmentSource: CrmEnrichmentSource;
    translationKeys: CrmEnrichmentI18nKeys;
    showEnrichmentLevel?: boolean;
    onSelectJobObject: (object: CrmEnrichmentJobObject) => void;
    onSelectJobRefreshPeriod: (period: CrmEnrichmentJobRefreshPeriod) => void;
    onSelectLeadScoreRefreshDataInterval?: (interval: LeadScoreRefreshDataInterval | "") => void;
    onSelectCountry: (country: ICountryObject) => void;
    onSelectEnrichmentSource: (source: CrmEnrichmentSource) => void;
};

const CrmEnrichmentJobSettings = ({
    jobObjectOptions,
    selectedJobType,
    selectedJobObject,
    initialJobObject,
    selectedJobRefreshPeriod,
    selectedLeadScoreRefreshDataInterval,
    selectedEnrichmentSource,
    selectedCountry,
    translationKeys,
    showEnrichmentLevel = true,
    onSelectJobRefreshPeriod,
    onSelectLeadScoreRefreshDataInterval,
    onSelectJobObject,
    onSelectCountry,
    onSelectEnrichmentSource,
}: Props) => {
    const leadScoreRefreshDataIntervalDropdown = (
        <StyledCrmEnrichmentJobDropdownWrapper>
            <Caption
                captionKey="si.crm-enrichment.job-wizard.settings.lead-score-refresh.caption"
                tooltipKey="si.crm-enrichment.job-wizard.settings.lead-score-refresh.tooltip"
            />

            <StyledCrmEnrichmentJobSetting>
                <LeadScoreRefreshDataIntervalDropdown
                    selectedLeadScoreRefreshDataInterval={selectedLeadScoreRefreshDataInterval ?? ""}
                    translationKeys={translationKeys}
                    onSelectLeadScoreRefreshDataInterval={onSelectLeadScoreRefreshDataInterval}
                />
            </StyledCrmEnrichmentJobSetting>
        </StyledCrmEnrichmentJobDropdownWrapper>
    );

    const scheduleDropdowns = (
        <Flex gap="8px">
            <StyledCrmEnrichmentJobDropdownWrapper>
                <Caption
                    captionKey="si.crm-enrichment.job-wizard.settings.refresh.caption"
                    tooltipKey="si.crm-enrichment.job-wizard.settings.refresh.tooltip"
                />

                <StyledCrmEnrichmentJobSetting>
                    <RefreshPeriodDropdown
                        selectedJobRefreshPeriod={selectedJobRefreshPeriod}
                        translationKeys={translationKeys}
                        onSelectJobRefreshPeriod={onSelectJobRefreshPeriod}
                    />
                </StyledCrmEnrichmentJobSetting>
            </StyledCrmEnrichmentJobDropdownWrapper>

            {onSelectLeadScoreRefreshDataInterval ? (
                <StyledScheduleDropdownWrapper>{leadScoreRefreshDataIntervalDropdown}</StyledScheduleDropdownWrapper>
            ) : null}
        </Flex>
    );

    return (
        <>
            <StyledCrmEnrichmentJobSetting>
                <Caption captionKey="si.crm-enrichment.job-editor.object" />

                <JobObjectSwitcher
                    options={jobObjectOptions}
                    initialJobObject={initialJobObject}
                    selectedJobObject={selectedJobObject}
                    translationKeys={translationKeys}
                    onSelectJobObject={onSelectJobObject}
                />
            </StyledCrmEnrichmentJobSetting>

            {showEnrichmentLevel ? (
                <div>
                    <Caption
                        captionKey="si.crm-enrichment.job-wizard.settings.enrichment-level.caption"
                        tooltipKey="si.crm-enrichment.job-wizard.settings.enrichment-level.tooltip"
                    />

                    <EnrichmentLevelRadio
                        selectedEnrichmentSource={selectedEnrichmentSource}
                        onSelectEnrichmentSource={onSelectEnrichmentSource}
                    />
                </div>
            ) : null}

            {selectedJobType === "scheduled" ? scheduleDropdowns : null}

            <div>
                <Caption
                    captionKey="si.crm-enrichment.job-wizard.settings.traffic.caption"
                    tooltipKey="si.crm-enrichment.job-wizard.settings.traffic.tooltip"
                />

                <StyledCrmEnrichmentJobSetting className={COUNTRY_DD_ID}>
                    <AllowedCountriesDropdown
                        appendTo={`.${COUNTRY_DD_ID}`}
                        dataAutomation="crm-enrichment-countries-dd"
                        selectedCountry={selectedCountry}
                        dropdownPopupHeight={COUNTRIES_DD_HEIGHT}
                        hasSearch
                        onCountrySelect={onSelectCountry}
                    />
                </StyledCrmEnrichmentJobSetting>
            </div>
        </>
    );
};

export default CrmEnrichmentJobSettings;
